import { KeyboardArrowDown, RemoveCircle } from '@mui/icons-material';
import { Button, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRemoveFromRole } from '~/api/roles';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { useConfirmDialog } from '~/hooks/dialogs';
import type { RoleUserList__Role } from '../RoleUserList.generated';

export interface BulkActionsMenuProps {
  role: RoleUserList__Role;
  selectedIds: GridRowSelectionModel;
}

export const BulkActionsMenu = ({ role, selectedIds }: BulkActionsMenuProps) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLButtonElement>(null);

  const [open, setOpen] = useState(false);

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const [removeUsers] = useRemoveFromRole({
    variables: {
      roleId: role.id,
      userIds: selectedIds as number[],
    },
  });

  if (!selectedIds.length) return null;

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        ref={ref}
        sx={(theme) => ({
          border: 1,
          borderColor: 'borders.light',
          color: theme.palette.primary.dark,
          fontWeight: 500,
          justifyContent: 'space-between',
          width: 200,
          '&, &:hover': {
            background: theme.palette.common.white,
          },
        })}
        variant="outlined"
      >
        {t('common.buttons.bulk_actions')}
        <KeyboardArrowDown />
      </Button>

      <Menu anchorEl={ref.current} open={open} onClose={() => setOpen(false)}>
        <MenuItem
          onClick={async () => {
            setOpen(false);
            if (!(await confirmDelete())) return;
            await removeUsers();
          }}
          sx={{ width: 200 }}
        >
          <ListItemIcon>
            <RemoveCircle color="error" fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t('pages.roles.dialogs.remove_users.menu_item')}
            primaryTypographyProps={{ color: 'error' }}
          />
        </MenuItem>
      </Menu>

      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('pages.roles.dialogs.remove_users.confirm')}
        deleteConfirm
        prompt={t('pages.roles.dialogs.remove_users.prompt', {
          count: selectedIds.length,
          role_name: role.name,
        })}
        title={t('pages.roles.dialogs.remove_users.title')}
      />
    </>
  );
};
